import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    CompanySummary,
    PageResponse,
    UserSummary,
    fetchCompanies,
    fetchPolarisUsers,
    saveCompanyInstantChat,
    saveUserInstantChat,
} from "../services/polarisService";
import { instantChatAccessNotice, mergeInstantChatReviewers } from "./instantChatReviewers";
import { formatRoleLabel, isSchoolChatRole } from "../utils/roleLabels";
import "../styles/polaris-admin.css";

const PAGE_SIZE = 20;
type PageTab = "companies" | "users";

function chatEnabled(value: boolean | undefined): boolean {
    return value !== false;
}

export default function PolarisInstantChatAdminPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab: PageTab = searchParams.get("tab") === "users" ? "users" : "companies";
    const [companyPage, setCompanyPage] = useState(0);
    const [userPage, setUserPage] = useState(0);
    const [companies, setCompanies] = useState<PageResponse<CompanySummary> | null>(null);
    const [users, setUsers] = useState<PageResponse<UserSummary> | null>(null);
    const [companyName, setCompanyName] = useState(searchParams.get("companyName") ?? "");
    const [userQuery, setUserQuery] = useState(searchParams.get("email") ?? "");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<string | null>(null);
    const [reviewerInputs, setReviewerInputs] = useState<Record<number, string>>({});
    const companyFilterRef = useRef(companyName);
    const userFilterRef = useRef(userQuery);
    companyFilterRef.current = companyName;
    userFilterRef.current = userQuery;

    const loadCompanies = useCallback(async (page: number, name: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchCompanies(page, PAGE_SIZE, name.trim() ? { companyName: name.trim() } : undefined);
            setCompanies(result);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load organisations.");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadUsers = useCallback(async (page: number, email: string) => {
        setLoading(true);
        setError(null);
        try {
            const trimmed = email.trim();
            const result = await fetchPolarisUsers(
                page,
                PAGE_SIZE,
                trimmed ? { email: trimmed } : undefined,
            );
            setUsers(result);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not load users.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tab === "companies") {
            void loadCompanies(companyPage, companyName);
        }
    }, [tab, companyPage, loadCompanies, companyName]);

    useEffect(() => {
        if (tab === "users") {
            void loadUsers(userPage, userQuery);
        }
    }, [tab, userPage, loadUsers, userQuery]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            if (tab === "companies" && companyFilterRef.current === companyName) {
                setCompanyPage(0);
            }
        }, 300);
        return () => window.clearTimeout(handle);
    }, [companyName, tab]);

    useEffect(() => {
        const handle = window.setTimeout(() => {
            if (tab === "users" && userFilterRef.current === userQuery) {
                setUserPage(0);
            }
        }, 300);
        return () => window.clearTimeout(handle);
    }, [userQuery, tab]);

    const setTab = (next: PageTab) => {
        setNotice(null);
        setError(null);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("tab", next);
        setSearchParams(nextParams, { replace: true });
    };

    const persistCompany = async (
        company: CompanySummary,
        enabled: boolean,
        reviewers: string[],
    ): Promise<CompanySummary | null> => {
        setSavingId(`company-${company.companyId}`);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveCompanyInstantChat(company.companyId, enabled, reviewers);
            setCompanies((current) =>
                current
                    ? {
                          ...current,
                          content: current.content.map((row) =>
                              row.companyId === saved.companyId ? { ...row, ...saved } : row,
                          ),
                      }
                    : current,
            );
            setNotice(
                enabled
                    ? `Instant Chat is on for ${saved.companyName}.`
                    : instantChatAccessNotice(
                          saved.companyName ?? "this organisation",
                          saved.instantChatReviewerUsernames ?? [],
                      ),
            );
            return saved;
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not update Instant Chat for this organisation.");
            return null;
        } finally {
            setSavingId(null);
        }
    };

    const toggleCompany = async (company: CompanySummary, enabled: boolean) => {
        await persistCompany(company, enabled, company.instantChatReviewerUsernames ?? []);
    };

    const addReviewer = (company: CompanySummary) => {
        const pending = reviewerInputs[company.companyId] ?? "";
        const current = company.instantChatReviewerUsernames ?? [];
        const next = mergeInstantChatReviewers(current, pending);
        if (next.length === current.length) {
            setReviewerInputs((inputs) => ({ ...inputs, [company.companyId]: "" }));
            return;
        }
        void persistCompany(company, false, next).then((saved) => {
            if (saved) {
                setReviewerInputs((inputs) => ({ ...inputs, [company.companyId]: "" }));
            }
        });
    };

    const removeReviewer = (company: CompanySummary, username: string) => {
        const next = (company.instantChatReviewerUsernames ?? []).filter((item) => item !== username);
        void persistCompany(company, false, next);
    };

    const saveReviewers = (company: CompanySummary) => {
        const next = mergeInstantChatReviewers(
            company.instantChatReviewerUsernames ?? [],
            reviewerInputs[company.companyId] ?? "",
        );
        void persistCompany(company, false, next).then((saved) => {
            if (saved) {
                setReviewerInputs((inputs) => ({ ...inputs, [company.companyId]: "" }));
            }
        });
    };

    const toggleUser = async (user: UserSummary, enabled: boolean) => {
        setSavingId(`user-${user.userId}`);
        setError(null);
        setNotice(null);
        try {
            const saved = await saveUserInstantChat(user.userId, enabled);
            setUsers((current) =>
                current
                    ? {
                          ...current,
                          content: current.content.map((row) =>
                              row.userId === saved.userId ? { ...row, ...saved } : row,
                          ),
                      }
                    : current,
            );
            const name = [saved.firstName, saved.lastName].filter(Boolean).join(" ") || saved.email;
            setNotice(enabled ? `Instant Chat is on for ${name}.` : `Instant Chat is off for ${name}.`);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Could not update Instant Chat for this user.");
        } finally {
            setSavingId(null);
        }
    };

    const companyRows = companies?.content ?? [];
    const userRows = useMemo(() => users?.content ?? [], [users]);

    return (
        <section className="pa-section" data-testid="polaris-instant-chat-admin-page">
            <h2 className="pa-section-title">Instant Chat</h2>
            <p className="pa-muted">
                Choose which organisations and people can see and use Instant Chat. A person can use
                chat when it is on for both their school and their account. If Instant Chat is off for
                a school, reviewers you list can still use it.
            </p>
            {error && <div className="pa-error">{error}</div>}
            {notice && (
                <div className="pa-success" role="status">
                    {notice}
                </div>
            )}

            <div className="pa-tabs" role="tablist" aria-label="Instant Chat sections">
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "companies"}
                    className={`pa-tab${tab === "companies" ? " active" : ""}`}
                    onClick={() => setTab("companies")}
                >
                    Companies
                </button>
                <button
                    type="button"
                    role="tab"
                    aria-selected={tab === "users"}
                    className={`pa-tab${tab === "users" ? " active" : ""}`}
                    onClick={() => setTab("users")}
                >
                    Users
                </button>
            </div>

            {tab === "companies" ? (
                <div className="pa-table-wrapper">
                    <label className="pa-filter-label" htmlFor="instant-chat-company-filter">
                        Organisation
                    </label>
                    <input
                        id="instant-chat-company-filter"
                        className="pa-filter-input"
                        placeholder="Filter by name…"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                    />
                    {loading && <div className="pa-loading">Loading organisations…</div>}
                    {!loading && (
                        <table className="pa-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th>School</th>
                                    <th>Instant Chat</th>
                                </tr>
                            </thead>
                            <tbody>
                                {companyRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} style={{ textAlign: "center", padding: "24px", color: "var(--muted)" }}>
                                            No organisations found
                                        </td>
                                    </tr>
                                ) : (
                                    companyRows.map((company) => {
                                        const enabled = chatEnabled(company.instantChatEnabled);
                                        const reviewers = company.instantChatReviewerUsernames ?? [];
                                        const saving = savingId === `company-${company.companyId}`;
                                        return (
                                            <Fragment key={company.companyId}>
                                                <tr>
                                                    <td>{company.companyName ?? "—"}</td>
                                                    <td>{company.schoolName ?? "—"}</td>
                                                    <td>
                                                        <label className="pa-assist-toggle">
                                                            <input
                                                                type="checkbox"
                                                                checked={enabled}
                                                                disabled={saving}
                                                                onChange={(e) =>
                                                                    void toggleCompany(company, e.target.checked)
                                                                }
                                                                aria-label={`Instant Chat for ${company.companyName ?? "organisation"}`}
                                                                data-testid={`instant-chat-company-${company.companyId}`}
                                                            />
                                                            <span>{enabled ? "On" : "Off"}</span>
                                                        </label>
                                                    </td>
                                                </tr>
                                                {!enabled && (
                                                    <tr>
                                                        <td colSpan={3}>
                                                            <div className="pa-assist-reviewers">
                                                                <p className="pa-assist-reviewers-title">Reviewers</p>
                                                                <p className="pa-muted">
                                                                    These people can still use Instant Chat while it is
                                                                    off for this organisation, as long as Instant Chat
                                                                    is on for their account.
                                                                </p>
                                                                <div className="pa-assist-evaluator-row">
                                                                    <input
                                                                        className="pa-filter-input"
                                                                        type="text"
                                                                        value={reviewerInputs[company.companyId] ?? ""}
                                                                        placeholder="username@school.edu"
                                                                        aria-label={`Reviewer username for ${company.companyName ?? "organisation"}`}
                                                                        onChange={(e) =>
                                                                            setReviewerInputs((inputs) => ({
                                                                                ...inputs,
                                                                                [company.companyId]: e.target.value,
                                                                            }))
                                                                        }
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === "Enter") {
                                                                                e.preventDefault();
                                                                                addReviewer(company);
                                                                            }
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        className="pa-page-btn"
                                                                        disabled={saving}
                                                                        onClick={() => addReviewer(company)}
                                                                    >
                                                                        Add
                                                                    </button>
                                                                </div>
                                                                {reviewers.length > 0 ? (
                                                                    <ul className="pa-assist-evaluator-list">
                                                                        {reviewers.map((username) => (
                                                                            <li
                                                                                key={username}
                                                                                className="pa-assist-evaluator-chip"
                                                                            >
                                                                                <span>{username}</span>
                                                                                <button
                                                                                    type="button"
                                                                                    className="pa-assist-remove"
                                                                                    aria-label={`Remove reviewer ${username}`}
                                                                                    disabled={saving}
                                                                                    onClick={() =>
                                                                                        removeReviewer(company, username)
                                                                                    }
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                ) : (
                                                                    <p className="pa-assist-empty">
                                                                        No reviewers yet. Add a username from this
                                                                        organisation.
                                                                    </p>
                                                                )}
                                                                <div className="pa-assist-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="pa-page-btn active"
                                                                        disabled={saving}
                                                                        onClick={() => saveReviewers(company)}
                                                                    >
                                                                        {saving ? "Saving…" : "Save reviewers"}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                    {companies && companies.totalPages > 1 && (
                        <div className="pa-pagination">
                            <button
                                type="button"
                                className="pa-page-btn"
                                disabled={companyPage === 0}
                                onClick={() => setCompanyPage((p) => Math.max(0, p - 1))}
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                className="pa-page-btn"
                                disabled={companyPage >= companies.totalPages - 1}
                                onClick={() => setCompanyPage((p) => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="pa-table-wrapper">
                    <label className="pa-filter-label" htmlFor="instant-chat-user-filter">
                        Email
                    </label>
                    <input
                        id="instant-chat-user-filter"
                        className="pa-filter-input"
                        placeholder="Filter by email…"
                        value={userQuery}
                        onChange={(e) => setUserQuery(e.target.value)}
                    />
                    {loading && <div className="pa-loading">Loading users…</div>}
                    {!loading && (
                        <table className="pa-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Company</th>
                                    <th>Instant Chat</th>
                                </tr>
                            </thead>
                            <tbody>
                                {userRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: "center", padding: "24px", color: "var(--muted)" }}>
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    userRows.map((user) => {
                                        const name =
                                            [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
                                        const companyOff = user.companyInstantChatEnabled === false;
                                        return (
                                            <tr key={user.userId}>
                                                <td>{name}</td>
                                                <td>{user.email}</td>
                                                <td>{formatRoleLabel(user.role)}</td>
                                                <td>{user.companyName ?? "—"}</td>
                                                <td>
                                                    <label className="pa-assist-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={chatEnabled(user.instantChatEnabled)}
                                                            disabled={savingId === `user-${user.userId}` || !isSchoolChatRole(user.role)}
                                                            onChange={(e) => void toggleUser(user, e.target.checked)}
                                                            aria-label={`Instant Chat for ${user.email}`}
                                                            data-testid={`instant-chat-user-${user.userId}`}
                                                        />
                                                        <span>{chatEnabled(user.instantChatEnabled) ? "On" : "Off"}</span>
                                                    </label>
                                                    {companyOff && (
                                                        <p className="pa-muted">
                                                            Off for this organisation unless they are a reviewer
                                                        </p>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                    {users && users.totalPages > 1 && (
                        <div className="pa-pagination">
                            <button
                                type="button"
                                className="pa-page-btn"
                                disabled={userPage === 0}
                                onClick={() => setUserPage((p) => Math.max(0, p - 1))}
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                className="pa-page-btn"
                                disabled={userPage >= users.totalPages - 1}
                                onClick={() => setUserPage((p) => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
